import { useEffect } from "react";
import type { FieldErrors, UseFormReturn } from "react-hook-form";
import { useFormRegistry, selectRegister } from "@stores/formRegistry";
import { mapApiErrorsToForm } from "@helpers/formErrorMapper";

function normalizeArrayErrors(methods: UseFormReturn<any>) {
  const errors = methods.formState.errors;

  Object.keys(errors).forEach((key) => {
    const error = errors[key] as FieldErrors & { root?: { type?: string; message?: string } };
    if (error && "root" in error && error.root?.message && !("message" in error)) {
      methods.clearErrors(key);
      methods.setError(key, {
        type: error.root.type || "custom",
        message: error.root.message,
      });
    }
  });
}

export function useFormRegistration(
  formName: string,
  methods: UseFormReturn<any>,
  getData?: () => Record<string, unknown> | null,
  cancel?: () => void,
) {
  const register = useFormRegistry(selectRegister);

  useEffect(() => {
    const unregister = register(formName, {
      validate: async () => {
        const isValid = await methods.trigger();
        if (!isValid) {
          normalizeArrayErrors(methods);
          // Expected UX: the user advanced with an invalid form, which the UI
          // already surfaces to them. Use console.debug so Sentry's
          // captureConsoleIntegration (warn/error only) doesn't promote this to
          // an issue. Serialize per-field messages so the local log is readable
          // instead of "[object Object]".
          const fieldErrors = Object.fromEntries(
            Object.entries(methods.formState.errors).map(([field, error]) => [
              field,
              (error as { message?: string })?.message ?? "invalid",
            ]),
          );
          console.debug(`[FormRegistration] "${formName}" validation failed:`, JSON.stringify(fieldErrors));
        }
        return isValid;
      },
      getData,
      cancel,
      setServerError: (error: unknown) => mapApiErrorsToForm(error, methods),
    });
    return unregister;
  }, [formName, methods, register, getData, cancel]);
}
